<template>
  <button
    v-if="loading"
    :class="classList"
    tabindex="0"
    @click.prevent>
      <div class="a-button__spinner"></div>
    <slot />
  </button>
  <button
    v-else-if="subtype === 'submit'"
    :class="classList"
    tabindex="0">
      <slot />
  </button>
  <a
    v-else-if="disabled"
    :class="classList"
    tabindex="0">
    <slot />
  </a>
  <!--Should be a button-->
  <a
    v-else-if="action && !disabled && !to"
    :class="classList"
    tabindex="0"
    @click.stop="action">
      <span v-if="tooltip" class="a-button__tooltip">{{ tooltip }}</span>
    <slot />
  </a>
  <!--This appears to be unnecessary-->
  <a
    v-else-if="href === '#'"
    :class="classList"
    tabindex="0"
    :href="href"
    @click.prevent>
    <slot />
  </a>
  <a
    v-else-if="href"
    :class="classList"
    tabindex="0"
    :href="href"
    :target="target">
      <span v-if="tooltip" class="a-button__tooltip">{{ tooltip }}</span>
    <slot />
  </a>
  <RouterLink
    v-else-if="to && !action"
    :class="classList"
    tabindex="0"
    :to="to">
      <slot />
  </RouterLink>
  <RouterLink
    v-else-if="to && action"
    :class="classList"
    tabindex="0"
    :to="to"
    @click.native="action">
      <slot />
  </RouterLink>

  <!-- if all else fails do the ui and bind all events -->
  <a
    v-else
    :class="classList"
    tabindex="0"
    v-on="$listeners">
      <span v-if="tooltip" class="a-button__tooltip">{{ tooltip }}</span>
    <slot />
  </a>
  <!-- Lets just emit the event. -->

</template>

<script>
export default {
  // Should split into button and link
  name: 'VButton',
  introduction: 'A button with styling and a loading spinner.',
  props: {
    disabled: {
      type: Boolean,
      default: false,
      note: 'Toggles the button being disabled.',
    },
    iconOnly: {
      type: Boolean,
      default: false,
      note: 'Styles button to take an icon only.',
    },
    transition: {
      type: Boolean,
      default: false,
      note: 'Toggles CSS ease transition (needs review, should probably be the default style).',
    },
    fullwidth: {
      type: Boolean,
      default: false,
      note: 'Forces button to take the full width of the parent container',
    },
    type: {
      type: String,
      note: 'The styling class to apply.',
    },
    size: {
      type: String,
      note: 'The size of the button ("small" or "large").',
    },
    href: {
      type: String,
      note: 'Triggers <a> tag to be rendered with the value of the href.',
    },
    target: {
      type: String,
      note: 'Applies to <a> tags and sets where to open the href.',
    },
    action: {
      type: Function,
      note: 'Executed on click (needs review).',
    },
    to: {
      type: [String, Object],
      note: 'The page router-link links to in the router (needs review could be merged with href).',
    },
    subtype: {
      type: String,
      note: 'If equals "submit" then a button element is rendered and the default event is allowed (needs review).',
    },
    loading: {
      type: Boolean,
      default: false,
      note: 'Toggles the button to disable and show a spinner.',
    },
    rounded: {
      type: Boolean,
      default: false,
      note: 'Toggles the button to be round or straight edged',
    },
    tooltip: {
      type: String,
      note: 'A string to display on mouse over.',
    },
    grouping: {
      type: String,
      note: 'Adds grouping styling, can be "left", "middle" or "right".',
    },
  },
  computed: {
    classList() {
      const {
        type, size, disabled, loading, iconOnly, transition, fullwidth, rounded, grouping,
      } = this;
      return {
        'a-button': true,
        'a-button--disabled': disabled,
        'a-button--loading': loading,
        'a-button--icon-only': iconOnly,
        'a-button--rounded': rounded,
        'a-button--transition': transition,
        'a-button--full-width': fullwidth,
        'a-button--grouping': grouping,
        [`a-button--grouping-${grouping}`]: grouping,
        [`a-button--${type}`]: type,
        [`a-button--${size}`]: size,
      };
    },
  },
};
</script>

<style scoped>
  .a-button {
    --color-gradient-default: var(--signable-secondary-4-shade-3);
    --color-gradient-default-lighter: white; /* lighten($color-gradient-default, 5%); */
    --color-gradient-primary-1: var(--signable-primary-1);
    --color-gradient-primary-1-lighter: #00abe8; /* lighten($color-gradient-primary-1, 5%); */
    --color-gradient-primary-2: var(--signable-primary-2);
    --color-gradient-primary-2-lighter: #dce66a; /* lighten($color-gradient-primary-2, 15%); */
    --color-gradient-primary-3: var(--signable-secondary-1);
    --color-gradient-primary-3-lighter: #6269b6; /* lighten($color-gradient-primary-3, 10%); */

    background: var(--color-gradient-default);
    background: -moz-linear-gradient(top, var(--color-gradient-default-lighter) 0%, var(--color-gradient-default) 100%);
    background: -webkit-linear-gradient(top, var(--color-gradient-default-lighter) 0%, var(--color-gradient-default) 100%);
    background: linear-gradient(to bottom, var(--color-gradient-default-lighter) 0%, var(--color-gradient-default) 100%);
    background-size: calc(100% + 2px) calc(100% + 2px);
    background-position: -1px;
    border-width: 1px;
    border-color: transparent;
    padding: var(--spacing-small) var(--spacing-base);
    float: left;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    line-height: 1;
    text-decoration: none;
    color: inherit;
    font-size: inherit;
    font-family: inherit;
    position: relative;
    box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.15);
    transition: 0.2s all ease;
  }

  .a-button:focus {
    outline: none;
  }
  .a-button ::v-deep svg {
    margin-right: var(--spacing-small);
    float: left;
    height: 14px;
    width: 14px;
    pointer-events: none;
  }
  .a-button__spinner {
    border-radius: 50%;
    border: 2px solid var(--signable-primary-1-shade-1);
    border-top-color: var(--signable-primary-1-shade-2);
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.4), inset 0 0 5px 0 rgba(0, 0, 0, 0.4);
    height: 26px;
    width: 26px;
    top: 50%;
    left: 50%;
    position: relative;
    animation: spinner 1.5s linear infinite;
    transition: 1s ease;
    z-index: 2;
    position: absolute;
    transform: translate3d(-50%, -50%, 0);
    top: 50%;
    left: 50%;
  }
  /* modifiers */
  .a-button--cta-1 {
    background: var(--color-gradient-primary-1);
    background: -moz-linear-gradient(top, var(--color-gradient-primary-1-lighter) 0%, var(--color-gradient-primary-1) 100%);
    background: -webkit-linear-gradient(top, var(--color-gradient-primary-1-lighter) 0%, var(--color-gradient-primary-1) 100%);
    background: linear-gradient(to bottom, var(--color-gradient-primary-1-lighter) 0%, var(--color-gradient-primary-1) 100%);
    border: 1px solid transparent;
    box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.2);
    background-size: calc(100% + 2px) calc(100% + 2px);
    background-position: -1px;
    background-color: var(--color-gradient-primary-1);
    background-repeat: no-repeat;
    color: #fff;
  }
  .a-button--cta-1:hover {
    background: var(--color-gradient-primary-1);
    background: -moz-linear-gradient(top, var(--color-gradient-primary-1-lighter) 0%, var(--color-gradient-primary-1) 100%);
    background: -webkit-linear-gradient(top, var(--color-gradient-primary-1-lighter) 0%, var(--color-gradient-primary-1) 100%);
    background: linear-gradient(to bottom, var(--color-gradient-primary-1-lighter) 0%, var(--color-gradient-primary-1) 100%);
    border-color: transparent;
    background-color: var(--color-gradient-primary-1);
    background-size: calc(100% + 2px) calc(100% + 2px);
    background-repeat: no-repeat;
    background-position: -1px;
    background-position-y: -40px;
    color: #fff;
    border: 1px solid transparent;
    box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.4);
  }
  .a-button--cta-1:active {
    background: var(--color-gradient-primary-1);
    color: #fff;
    border: 1px solid transparent;
  }
  .a-button--cta-2 {
    background: var(--color-gradient-primary-2);
    background: -moz-linear-gradient(top, var(--color-gradient-primary-2-lighter) 0%, var(--color-gradient-primary-2) 100%);
    background: -webkit-linear-gradient(top, var(--color-gradient-primary-2-lighter) 0%, var(--color-gradient-primary-2) 100%);
    background: linear-gradient(to bottom, var(--color-gradient-primary-2-lighter) 0%, var(--color-gradient-primary-2) 100%);
    border: 1px solid transparent;
    box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.2);
    background-size: calc(100% + 2px) calc(100% + 2px);
    background-position: -1px;
    color: #fff;
  }
  .a-button--cta-2:hover {
    background: var(--color-gradient-primary-2-lighter);
    background: -moz-linear-gradient(top, var(--color-gradient-primary-2) 0%, var(--color-gradient-primary-2-lighter) 100%);
    background: -webkit-linear-gradient(top, var(--color-gradient-primary-2) 0%, var(--color-gradient-primary-2-lighter) 100%);
    background: linear-gradient(to bottom, var(--color-gradient-primary-2) 0%, var(--color-gradient-primary-2-lighter) 100%);
    color: #fff;
    border: 1px solid var(--signable-primary-2);
  }
  .a-button--cta-2:active {
    background: var(--signable-primary-2);
    color: #fff;
    border: 1px solid var(--signable-primary-2);
  }
  .a-button--cta-3 {
    background: var(--color-gradient-primary-3);
    background: -moz-linear-gradient(top, var(--color-gradient-primary-3-lighter) 0%, var(--color-gradient-primary-3) 100%);
    background: -webkit-linear-gradient(top, var(--color-gradient-primary-3-lighter) 0%, var(--color-gradient-primary-3) 100%);
    background: linear-gradient(to bottom, var(--color-gradient-primary-3-lighter) 0%, var(--color-gradient-primary-3) 100%);
    border: 1px solid transparent;
    box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.2);
    background-size: calc(100% + 2px) calc(100% + 2px);
    background-position: -1px;
    background-color: var(--color-gradient-primary-3);
    background-repeat: no-repeat;
    color: #fff;
  }
  .a-button--cta-3:hover {
    background: var(--color-gradient-primary-3);
    background: -moz-linear-gradient(top, var(--color-gradient-primary-3-lighter) 0%, var(--color-gradient-primary-3) 100%);
    background: -webkit-linear-gradient(top, var(--color-gradient-primary-3-lighter) 0%, var(--color-gradient-primary-3) 100%);
    background: linear-gradient(to bottom, var(--color-gradient-primary-3-lighter) 0%, var(--color-gradient-primary-3) 100%);
    border-color: transparent;
    background-color: var(--color-gradient-primary-3);
    background-size: calc(100% + 2px) calc(100% + 2px);
    background-repeat: no-repeat;
    background-position: -1px;
    background-position-y: -40px;
    color: #fff;
    border: 1px solid transparent;
    box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.4);
  }
  .a-button--cta-3:active {
    background: var(--signable-secondary-1);
    color: #fff;
    border: 1px solid var(--signable-secondary-1);
  }
  .a-button--outline-1 {
    border: 1px solid var(--signable-secondary-4);
    color: var(--signable-secondary-3);
  }
  .a-button--outline-1:hover,
  .a-button--outline-1:active  {
    color: #fff;
    border: 1px solid var(--signable-primary-1);
    background: var(--signable-primary-1);
    box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.4);
  }
  .a-button--outline-2 {
    border: 1px solid var(--signable-primary-2);
    color: #bbc922; /* darken($signable-primary-2, 5%); */
  }
  .a-button--outline-2:hover {
    color: #fff;
    background: var(--signable-primary-2);
    border-color: var(--signable-primary-2);
  }
  .a-button--outline-2:active {
    border-color: var(--signable-primary-2);
    background: var(--signable-primary-2-shade-1);
    color: #fff;
  }
  .a-button--outline-3 {
    border: 1px solid var(--signable-secondary-1);
    color: var(--signable-secondary-1);
  }
  .a-button--outline-3:hover {
    color: #fff;
    background: var(--signable-secondary-1);
    border-color: var(--signable-secondary-1);
  }
  .a-button--outline-3:active {
    border-color: var(--signable-secondary-1);
    background: var(--color-gradient-primary-3-lighter);
    color: #fff;
  }
  .a-button--outline-4 {
    border: 1px solid var(--signable-secondary-2);
    color: var(--signable-secondary-2);
  }
  .a-button--outline-4:hover {
    color: #fff;
    background: var(--signable-secondary-2);
    border-color: var(--signable-secondary-2);
  }
  .a-button--outline-4:active {
    border-color: var(--signable-secondary-2);
    background: var(--color-gradient-primary-3-lighter);
    color: #fff;
  }
  .a-button--outline-success {
    border-color: var(--color-success);
    color: var(--color-success);
  }
  .a-button--outline-success:hover {
    border-color: var(--color-success-dark);
    background: var(--color-success);
    color: #fff;
  }
  .a-button--outline-warning {
    border-color: var(--color-warning);
    color: var(--color-warning);
  }
  .a-button--outline-warning:hover {
    border-color: var(--color-warning-dark);
    background: var(--color-warning);
    color: #fff;
  }
  .a-button--outline-error {
    border-width: 1px;
    border: 1px solid var(--color-error);
    color: var(--color-error);
  }
  .a-button--outline-error:hover {
    border: 1px solid var(--color-error);
    background: var(--color-error);
    color: #fff;
  }
  .a-button--success {
    background: var(--color-success);
    color: #fff;
    border: 1px solid var(--color-success-dark);
  }
  .a-button--success:hover,
  .a-button--success:active {
    background: var(--color-success-dark);
    border-color: var(--color-success-dark);
    color: #fff;
  }
  .a-button--warning {
    background: var(--color-warning);
    color: #fff;
    border: 1px solid var(--color-warning-dark);
  }
  .a-button--warning:hover,
  .a-button--warning:active {
    background: var(--color-warning-dark);
    border-color: var(--color-warning-dark);
    color: #fff;
  }
  .a-button--error {
    background: #BC4134;
    background: -moz-linear-gradient(top, #E74C3C 0%, #BC4134 100%);
    background: -webkit-linear-gradient(top, #E74C3C 0%, #BC4134 100%);
    background: linear-gradient(to bottom, #E74C3C 0%, #BC4134 100%);
    border: 1px solid transparent;
    box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.2);
    background-size: calc(100% + 2px) calc(100% + 2px);
    background-position: -1px;
    background-color: #BC4134;
    background-repeat: no-repeat;
    color: #fff;
  }
  .a-button--error:hover {
    background: #BC4134;
    background: -moz-linear-gradient(top, #E74C3C 0%, #BC4134 100%);
    background: -webkit-linear-gradient(top, #E74C3C 0%, #BC4134 100%);
    background: linear-gradient(to bottom, #E74C3C 0%, #BC4134 100%);
    border-color: transparent;
    background-color: #BC4134;
    background-size: calc(100% + 2px) calc(100% + 2px);
    background-repeat: no-repeat;
    background-position: -1px;
    background-position-y: -40px;
    color: #fff;
    border: 1px solid transparent;
    box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.4);
  }
  .a-button--error:active {
    background: #BC4134;
    color: #fff;
    border: 1px solid transparent;
  }
  .a-button--large {
    font-size: 20px;
  }
  .a-button--large ::v-deep svg {
    height: 20px;
    width: 16px;
  }
  .a-button--small {
    font-size: var(--font-size-small);
    padding: var(--spacing-xs) var(--spacing-small);
  }
  .a-button--small svg {
    margin-right: var(--spacing-xs);
  }
  .a-button--input {
    height: 45px;
    padding: var(--spacing-base) var(--spacing-base);
  }
  .a-button--grouping {
    /* border-left-width: 0; */
    margin-left: 0;
    margin-right: 0;
  }
  .a-button--grouping:hover {
    border-left-width: solid 1px;
  }
  .a-button--grouping-left {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-left-width: 1px;
  }
  .a-button--grouping-right {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  .a-button--grouping-middle {
    border-radius: 0px;
    margin-left: 0px;
    margin-right: 0px;
  }
  .a-button--rounded {
    border-radius: 30px;
  }
  .a-button--icon-only {
    padding: var(--spacing-small);
    position: relative;
  }
  .a-button--icon-only:hover .a-button__tooltip {
    display: block;
  }
  .a-button--icon-only ::v-deep svg {
    margin-right: 0;
    height: 16px;
    width: 16px;
  }
  .a-button--icon-only .a-button__tooltip {
    position: absolute;
    display: none;
    font-size: 11px;
    top: 0;
    min-width: 100%;
    white-space: nowrap;
    background: #333;
    padding: 5px 7px;
    color: #fff;
    border-radius: 30px;
    position: absolute;
    transform: translate3d(-50%, 0, 0);
    top: 50%;
    left: 50%;
    top: -25px;
  }
  .a-button--icon-only.a-button--small {
    padding: calc(var(--spacing-xs) + 2);
  }
  .a-button--icon-only.a-button--small svg {
    margin-right: 0;
    height: 12px;
    width: 12px;
  }
  .a-button--disabled,
  .a-button--loading {
    background: var(--signable-secondary-4-shade-2);
    border: 1px solid transparent;
    color: var(--signable-secondary-3);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
  .a-button--disabled:hover,
  .a-button--disabled:active,
  .a-button--loading:hover,
  .a-button--loading:active {
    cursor: not-allowed;
    background: var(--signable-secondary-4-shade-2);
    border: 1px solid transparent;
    color: var(--signable-secondary-3);
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
  .a-button--transition {
    transition: 0.25s all ease;
  }
  .a-button--full-width {
    text-align: center;
    width: 100%;
  }
  .a-button--full-width ::v-deep svg {
    float: none;
    vertical-align: bottom;
  }

  .user-has-tabbed .a-button:active {
    outline: 2px solid var(--signable-primary-1-shade-1);
    outline-offset: 1px;
  }
  .user-has-tabbed .a-button:focus {
    outline: 2px solid var(--signable-primary-1-shade-1);
    outline-offset: 1px;
  }

  @keyframes spinner {
    0% {
      transform: translate(-50%, -50%) rotate(0deg);
    }
    100% {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }
</style>
